/**
 * TransactionRow (FE-2). One transaction line: date, payee, purpose, account,
 * note, category chip(s), PSP deep link (Amazon/PayPal, `lib/orderLinks`) and
 * signed amount. A "Bearbeiten" action opens a
 * detail modal (fetched via `useTransaction`) offering inline re-categorize /
 * skip / reopen and a free-text Notiz — the list's write path (§4.1, §7.3).
 */
import { useState, type ChangeEvent } from 'react';
import { ExternalLink, Paperclip, Pencil, StickyNote, Trash2, Upload } from 'lucide-react';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { BucketChip } from '../../components/ui/BucketChip.tsx';
import { BucketSelect } from '../../components/ui/BucketSelect.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.tsx';
import { Modal } from '../../components/ui/Modal.tsx';
import { Textarea } from '../../components/ui/Textarea.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { useOnlineStatus } from '../../context/OnlineStatusContext.tsx';
import { formatDate } from '../../lib/format.ts';
import { orderLink } from '../../lib/orderLinks.ts';
import { REVIEW_STATUS, SOURCE, STATUS } from '../../lib/labels.ts';
import {
  useReopenTransaction,
  useSkipTransaction,
  useRecategorizeTransaction,
  useTransaction,
  useUpdateTransaction,
} from '../../lib/hooks/useTransactions.ts';
import {
  useDeleteReceipt,
  useDetachReceipt,
  useUploadReceipt,
} from '../../lib/hooks/useReceipts.ts';
import type { BucketOut, Receipt, ReviewStatus, TransactionOut } from '../../lib/types.ts';

/** One attached receipt inside the "Belege" section: open / detach / delete. */
function ReceiptRow({ receipt }: { receipt: Receipt }) {
  const { toast } = useToast();
  const { guardOnline } = useOnlineStatus();
  const detach = useDetachReceipt();
  const del = useDeleteReceipt();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const busy = detach.isPending || del.isPending;

  function detachReceipt() {
    if (!guardOnline()) return;
    detach.mutate(receipt.id, {
      onSuccess: () => toast('Zuordnung entfernt.', { variant: 'success' }),
      onError: (error) =>
        toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
          variant: 'error',
        }),
    });
  }

  function deleteReceipt() {
    if (!guardOnline()) return;
    del.mutate(receipt.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast('Beleg gelöscht.', { variant: 'success' });
      },
      onError: (error) =>
        toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
          variant: 'error',
        }),
    });
  }

  return (
    <div className="flex items-center justify-between gap-2 py-2">
      <div className="min-w-0 flex-1">
        <a
          href={`/api/receipts/${receipt.id}/file`}
          target="_blank"
          rel="noreferrer"
          title="Beleg öffnen"
          className="block truncate text-sm font-medium text-slate-900 transition-colors duration-150 hover:text-sky-600 dark:text-slate-100 dark:hover:text-sky-400"
        >
          {receipt.file_name}
        </a>
        <p className="text-xs text-slate-400 dark:text-slate-500">
          {formatDate(receipt.created_at)} · {Math.round(receipt.size_bytes / 1024)} KB
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={detachReceipt}
          disabled={busy}
          className="rounded-md px-2 py-1 text-xs font-medium text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
        >
          Zuordnung entfernen
        </button>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={busy}
          aria-label="Beleg löschen"
          className="rounded-md p-1.5 text-slate-400 transition-colors duration-150 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-slate-500 dark:hover:bg-rose-950/50 dark:hover:text-rose-400"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Beleg löschen"
        message="Beleg wirklich löschen?"
        confirmLabel="Löschen"
        danger
        loading={del.isPending}
        onConfirm={deleteReceipt}
      />
    </div>
  );
}

/** "Beleg hinzufügen" upload affordance — direct attach to `transactionId`. */
function ReceiptUpload({ transactionId }: { transactionId: number }) {
  const { guardOnline } = useOnlineStatus();
  const { toast } = useToast();
  const upload = useUploadReceipt();
  const [error, setError] = useState<string | null>(null);

  function onFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;
    if (!guardOnline()) return;
    setError(null);
    upload.mutate(
      { file, transactionId },
      {
        onSuccess: () => toast('Beleg hochgeladen.', { variant: 'success' }),
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Fehler beim Hochladen.'),
      },
    );
  }

  return (
    <div className="space-y-1.5">
      <label
        className={`inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-sky-700 dark:bg-sky-500 dark:hover:bg-sky-400 ${
          upload.isPending ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        }`}
      >
        <Upload className="h-4 w-4" />
        {upload.isPending ? 'Lädt hoch …' : 'Beleg hinzufügen'}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          capture="environment"
          className="sr-only"
          onChange={onFileChange}
          disabled={upload.isPending}
        />
      </label>
      {error ? <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p> : null}
    </div>
  );
}

export interface TransactionRowProps {
  txn: TransactionOut;
  bucketMap: Map<number, BucketOut>;
}

const REVIEW_BADGE: Record<ReviewStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  reviewed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  skipped: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

function ReviewBadge({ status }: { status: ReviewStatus }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${REVIEW_BADGE[status]}`}
    >
      {REVIEW_STATUS[status]}
    </span>
  );
}

function CategoryCell({
  txn,
  bucketMap,
}: {
  txn: TransactionOut;
  bucketMap: Map<number, BucketOut>;
}) {
  if (txn.is_split) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        Aufgeteilt ({txn.bucket_ids.length})
      </span>
    );
  }
  if (txn.primary_bucket_id !== null) {
    return <BucketChip bucket={bucketMap.get(txn.primary_bucket_id) ?? null} size="sm" />;
  }
  return (
    <span className="text-xs text-slate-400 dark:text-slate-500">Nicht kategorisiert</span>
  );
}

export function TransactionRow({ txn, bucketMap }: TransactionRowProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [bucketId, setBucketId] = useState<number | null>(txn.primary_bucket_id);
  const [notes, setNotes] = useState(txn.notes ?? '');

  const detail = useTransaction(open ? txn.id : null);
  const recategorize = useRecategorizeTransaction();
  const skip = useSkipTransaction();
  const reopen = useReopenTransaction();
  const update = useUpdateTransaction();

  const busy = recategorize.isPending || skip.isPending || reopen.isPending;
  const payee = txn.counterparty_name || txn.normalized_payee || 'Ohne Empfänger';
  // Enriched card-acceptor (e.g. "Dulfs Burger") outranks the masking PSP
  // ("Adyen N.V.") for display; the PSP stays visible as secondary text.
  const displayPayee = txn.merchant_name || payee;
  const notesDirty = notes !== (txn.notes ?? '');
  const link = orderLink(txn);

  function openModal() {
    setBucketId(txn.primary_bucket_id);
    setNotes(txn.notes ?? '');
    setOpen(true);
  }

  function saveNotes() {
    const value = notes.trim();
    update.mutate(
      { id: txn.id, body: { notes: value === '' ? null : value } },
      {
        onSuccess: () => toast('Notiz gespeichert.', { variant: 'success' }),
        onError: (error) =>
          toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
            variant: 'error',
          }),
      },
    );
  }

  function assignCategory() {
    if (bucketId === null) return;
    recategorize.mutate(
      { id: txn.id, splits: [{ bucket_id: bucketId, amount: txn.amount }], source: 'grid' },
      {
        onSuccess: () => {
          toast('Kategorie zugewiesen.', { variant: 'success' });
          setOpen(false);
        },
        onError: (error) =>
          toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
            variant: 'error',
          }),
      },
    );
  }

  function skipTxn() {
    skip.mutate(txn.id, {
      onSuccess: () => {
        toast('Transaktion übersprungen.', { variant: 'success' });
        setOpen(false);
      },
      onError: (error) =>
        toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
          variant: 'error',
        }),
    });
  }

  function reopenTxn() {
    reopen.mutate(txn.id, {
      onSuccess: () => {
        toast('Transaktion zurückgesetzt.', { variant: 'success' });
        setOpen(false);
      },
      onError: (error) =>
        toast(error instanceof Error ? error.message : 'Fehler beim Speichern.', {
          variant: 'error',
        }),
    });
  }

  return (
    <>
      <div className="flex items-start justify-between gap-3 px-4 py-3.5 transition-colors duration-150 hover:bg-slate-50 sm:px-6 dark:hover:bg-slate-800/60">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-slate-900 dark:text-slate-100">
              {displayPayee}
            </span>
            {txn.merchant_name ? (
              <span className="shrink-0 truncate text-xs text-slate-400 dark:text-slate-500">
                über {payee}
              </span>
            ) : null}
            {txn.review_status !== 'reviewed' ? (
              <ReviewBadge status={txn.review_status} />
            ) : null}
          </div>
          {txn.purpose ? (
            <p className="truncate text-sm text-slate-500 dark:text-slate-400">{txn.purpose}</p>
          ) : null}
          {txn.notes ? (
            <p className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
              <StickyNote className="h-3.5 w-3.5 shrink-0 text-amber-500 dark:text-amber-400" />
              <span className="truncate italic">{txn.notes}</span>
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400 dark:text-slate-500">
            <span>{formatDate(txn.booking_date)}</span>
            <span aria-hidden>·</span>
            <span className="truncate">{txn.account_name}</span>
            <span aria-hidden>·</span>
            <CategoryCell txn={txn} bucketMap={bucketMap} />
            {link ? (
              <>
                <span aria-hidden>·</span>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-sky-600 transition-colors duration-150 hover:text-sky-700 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
                >
                  <ExternalLink className="h-3 w-3" aria-hidden />
                  {link.label}
                </a>
              </>
            ) : null}
            {txn.receipt_count > 0 ? (
              <>
                <span aria-hidden>·</span>
                <span className="inline-flex items-center gap-1 text-slate-400 dark:text-slate-500">
                  <Paperclip className="h-3 w-3" />
                  {txn.receipt_count}
                </span>
              </>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <AmountText value={txn.amount} showPlus />
          <button
            type="button"
            onClick={openModal}
            aria-label="Transaktion bearbeiten"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
          >
            <Pencil className="h-3.5 w-3.5" />
            Bearbeiten
          </button>
        </div>
      </div>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={displayPayee}
        description={`${formatDate(txn.booking_date)} · ${txn.account_name}`}
        size="md"
        footer={
          <>
            {txn.review_status !== 'pending' ? (
              <Button variant="ghost" onClick={reopenTxn} disabled={busy}>
                Zurücksetzen
              </Button>
            ) : null}
            {txn.review_status !== 'skipped' ? (
              <Button variant="secondary" onClick={skipTxn} disabled={busy}>
                Überspringen
              </Button>
            ) : null}
            <Button variant="primary" onClick={assignCategory} disabled={busy || bucketId === null}>
              Kategorie zuweisen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500 dark:text-slate-400">Betrag</span>
            <AmountText value={txn.amount} showPlus className="text-lg" />
          </div>

          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Status</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {REVIEW_STATUS[txn.review_status]}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Buchung</dt>
              <dd className="text-slate-900 dark:text-slate-100">{STATUS[txn.status]}</dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Herkunft</dt>
              <dd className="text-slate-900 dark:text-slate-100">{SOURCE[txn.source]}</dd>
            </div>
            {txn.value_date ? (
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Wertstellung</dt>
                <dd className="text-slate-900 dark:text-slate-100">
                  {formatDate(txn.value_date)}
                </dd>
              </div>
            ) : null}
            {txn.merchant_name ? (
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Händler</dt>
                <dd className="text-slate-900 dark:text-slate-100">{txn.merchant_name}</dd>
              </div>
            ) : null}
            {txn.booking_text ? (
              <div>
                <dt className="text-slate-500 dark:text-slate-400">Buchungstext</dt>
                <dd className="text-slate-900 dark:text-slate-100">{txn.booking_text}</dd>
              </div>
            ) : null}
            {txn.counterparty_iban ? (
              <div className="col-span-2">
                <dt className="text-slate-500 dark:text-slate-400">IBAN</dt>
                <dd className="break-all text-slate-900 dark:text-slate-100">
                  {txn.counterparty_iban}
                </dd>
              </div>
            ) : null}
          </dl>

          {txn.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {txn.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          <div className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800/70">
            <p className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
              Kategorie
            </p>
            {detail.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-sky-600 dark:border-slate-700 dark:border-t-sky-500" />
                Lade Details …
              </div>
            ) : detail.data && detail.data.splits.length > 1 ? (
              <div className="space-y-1.5">
                <div className="divide-y divide-slate-100 dark:divide-slate-800/70">
                  {detail.data.splits.map((split) => (
                    <div key={split.id} className="flex items-center justify-between py-2">
                      <BucketChip bucket={bucketMap.get(split.bucket_id) ?? null} size="sm" />
                      <AmountText value={split.amount} showPlus />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Die Auswahl unten ersetzt alle Aufteilungen durch eine Kategorie.
                </p>
              </div>
            ) : null}
            <BucketSelect value={bucketId} onChange={setBucketId} allowClear={false} />
          </div>

          <div className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800/70">
            <label
              htmlFor={`txn-notes-${txn.id}`}
              className="block text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500"
            >
              Notiz
            </label>
            <Textarea
              id={`txn-notes-${txn.id}`}
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Was steckt hinter dieser Transaktion? (z. B. Amazon: neue Kopfhörer)"
              disabled={update.isPending}
            />
            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={saveNotes}
                disabled={update.isPending || !notesDirty}
              >
                {update.isPending ? 'Speichert …' : 'Notiz speichern'}
              </Button>
            </div>
          </div>

          <div className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800/70">
            <p className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
              Belege
            </p>
            {detail.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-sky-600 dark:border-slate-700 dark:border-t-sky-500" />
                Lade Details …
              </div>
            ) : detail.data && detail.data.receipts.length > 0 ? (
              <div className="divide-y divide-slate-100 dark:divide-slate-800/70">
                {detail.data.receipts.map((receipt) => (
                  <ReceiptRow key={receipt.id} receipt={receipt} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 dark:text-slate-500">Keine Belege vorhanden</p>
            )}
            <ReceiptUpload transactionId={txn.id} />
          </div>
        </div>
      </Modal>
    </>
  );
}
